import { Disclosure } from "@headlessui/react";
import { Button, Icon, Typography } from "@/components/atoms";
import { SoloScenario, SoloScenarioFaqItem, stillStuckSuffix } from "../data/driverAppScenarios";
import { ScenarioCopy } from "./ScenarioCopy";

/**
 * Figma draws this as a link-style row — chevron on the left, info-blue label, no box — rather than
 * the boxed grey header the shared DisclosurePanel molecule renders, so it is built locally.
 */
const NotConnectingDisclosure = ({ faq }: { faq: SoloScenarioFaqItem[] }) => (
  <Disclosure as="div" className="flex flex-col gap-3">
    {({ open }) => (
      <>
        <Disclosure.Button className="flex w-fit items-center gap-2 text-left outline-none">
          <Icon name={open ? "ArrowDown2" : "ArrowRight2"} size="md" className="text-info" />
          <Typography variant="action" className="text-info">
            It&apos;s not connecting? &mdash; what went wrong?
          </Typography>
        </Disclosure.Button>
        <Disclosure.Panel className="flex flex-col gap-3 pl-8">
          {faq.map((item) => (
            <Typography key={item.symptom} variant="paragraph" className="text-neutral-dark-gray">
              <span className="font-semibold text-neutral-black">{item.symptom}</span> {item.fix}
            </Typography>
          ))}
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            <a href="mailto:support@rideminder.com" target="_blank" rel="noreferrer" className="text-info underline">
              Still stuck? Email Support
            </a>
            {stillStuckSuffix}
          </Typography>
        </Disclosure.Panel>
      </>
    )}
  </Disclosure>
);

export interface DriverAppScenarioPanelProps {
  scenario: SoloScenario;
  /** The operator's own portal login email, substituted into the copy's "[operator email]". */
  operatorEmail?: string;
  onBack: () => void;
}

export const DriverAppScenarioPanel = ({ scenario, operatorEmail, onBack }: DriverAppScenarioPanelProps) => {
  const { intro, checklist, steps, warning, faq } = scenario;

  return (
    <div className="flex flex-col gap-4 p-4">
      <Button
        onClick={onBack}
        variant="tertiary-light"
        size="xs"
        startIcon={<Icon name="ArrowLeft2" size="md" />}
        className="!w-fit !p-0 text-neutral-dark-gray hover:bg-white"
      >
        Back
      </Button>

      {intro?.map((paragraph, index) => (
        <Typography key={index} variant="paragraph" className="text-neutral-dark-gray">
          <ScenarioCopy runs={paragraph} operatorEmail={operatorEmail} />
        </Typography>
      ))}

      <div className="flex flex-col gap-2 border-t border-neutral-mid-gray pt-4">
        <Typography variant="small" className="text-neutral-silver-gray">
          BEFORE YOU PICK UP YOUR PHONE
        </Typography>
        {checklist.map((item) => (
          <div key={item.title} className="flex gap-2">
            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] border-[1.25px] border-primary bg-primary-light">
              <Icon isCustom name="check" size="xs" className="text-primary" />
            </div>
            <div className="flex flex-1 flex-col">
              <Typography variant="action" className="text-neutral-dark-gray">
                {item.title}
              </Typography>
              <Typography variant="paragraph" className="text-neutral-silver-gray">
                <ScenarioCopy runs={item.description} operatorEmail={operatorEmail} />
              </Typography>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-4 border-y border-neutral-mid-gray py-4">
        <div className="flex flex-col gap-2">
          <Typography variant="small" className="text-neutral-silver-gray">
            WHAT HAPPENS ON YOUR PHONE
          </Typography>
          {/* The connector is a dashed border on the list, so it spans exactly the numbered rows and
              needs no absolutely positioned line to be kept in sync with the row count. */}
          <ol className="ml-2.5 flex flex-col gap-2 border-l border-dashed border-neutral-mid-gray">
            {steps.map((step, index) => (
              <li key={index} className="-ml-2.5 flex items-center gap-3">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-tint-light">
                  <Typography variant="small" className="text-vivid-purple-dark">
                    {index + 1}
                  </Typography>
                </span>
                <Typography variant="paragraph" className="flex-1 text-neutral-dark-gray">
                  <ScenarioCopy runs={step} operatorEmail={operatorEmail} />
                </Typography>
              </li>
            ))}
          </ol>
        </div>

        {warning && (
          <div className="flex flex-col gap-1 rounded-lg border border-warning bg-warning-light p-2">
            <Typography variant="action" className="text-warning-dark">
              {warning.title}
            </Typography>
            <Typography variant="paragraph" className="text-warning-dark">
              <ScenarioCopy runs={warning.body} operatorEmail={operatorEmail} />
            </Typography>
          </div>
        )}
      </div>

      <NotConnectingDisclosure faq={faq} />
    </div>
  );
};
